import React, { useState, useEffect, useRef } from "react";

const highlightMentions = (text) => {
  const mentionPattern = /@([\w.]+)/g;
  const parts = text.split(mentionPattern);

  return parts.map((part, index) => {
    if (index % 2 === 1) {
      return (
        <span key={index} className="text-jiojbm50">
          @{part.toLowerCase()}
        </span>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

export const Comments = ({ ticketId, org, authToken, users }) => {
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedComment, setEditedComment] = useState("");
  const [mentionSearch, setMentionSearch] = useState(""); // Track search input after '@'
  const [filteredUsers, setFilteredUsers] = useState([]); // Filtered users based on search
  const commentsEndRef = useRef(null); // Reference to the last comment
  const commentsContainerRef = useRef(null);

  useEffect(() => {
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/API/V2/Tickets/${ticketId}/comments`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      },
    )
      .then((response) => response.json())
      .then((responseData) => {
        setComments(responseData);
      });
  }, []);

  useEffect(() => {
    if (commentsContainerRef.current) {
      commentsContainerRef.current.scrollTop = 0;
    }
  }, [comments]);

  useEffect(() => {
    if (mentionSearch) {
      setFilteredUsers(
        users.filter((user) =>
          user.email.toLowerCase().includes(mentionSearch.toLowerCase()),
        ),
      );
    } else {
      setFilteredUsers([]);
    }
  }, [mentionSearch, users]);

  function handleAddComment() {
    if (!comment.trim()) {
      alert("Comment cannot be empty");
      return;
    }

    const data = {
      commentText: comment,
      org,
    };

    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/API/V2/Tickets/${ticketId}/comments`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(data),
      },
    )
      .then((response) => response.json())
      .then((responseData) => {
        if (!responseData.error) {
          setComments((prevComments) => [
            ...prevComments,
            responseData.comment,
          ]);
          setComment(""); // Clear the input field after adding the comment
        }
      })
      .catch((error) => {
        console.log("Fetch error:", error);
      });
  }

  function handleChange(e) {
    const value = e.target.value;
    setComment(value);

    // Check if '@' is typed and handle the search part
    const lastAt = value.lastIndexOf("@");
    if (lastAt !== -1) {
      setMentionSearch(value.slice(lastAt + 1)); // Update search after '@'
    } else {
      setMentionSearch("");
    }
  }

  function handleMentionSelect(user) {
    const lastAt = comment.lastIndexOf("@");
    const newComment =
      comment.slice(0, lastAt) + "@" + user.email.slice(0, -8) + " "; // Replace @ with the email
    setComment(newComment);
    setMentionSearch(""); // Clear search after selection
    setFilteredUsers([]); // Clear filtered users
  }

  const sortedComments = [...comments].reverse();

  return (
    <div className="h-full flex flex-col">
      <div className="flex flex-col bg-white rounded-2xl shadow-sm border border-gray-100 p-4 pr-0 flex-1 min-h-0 text-xs">
        {/* Add comment area — fixed at top */}
        <div className="flex-shrink-0">
          <div className="relative mr-4">
            <textarea
              className="w-full h-14 border p-1 pt-2 px-3 border-gray60 resize-none rounded-lg focus:outline-none focus:border-jiojbm60 disabled:bg-white disabled:border-gray-300 disabled:cursor-not-allowed"
              placeholder="Add a Comment. Use @ to mention a person"
              value={comment}
              onChange={handleChange}
            />
            {mentionSearch && filteredUsers.length > 0 && (
              <div className="absolute z-10 bg-white border rounded shadow-lg w-full mt-1">
                {filteredUsers.map((user, index) => (
                  <div
                    key={index}
                    className="px-3 py-1.5 cursor-pointer hover:bg-gray-200"
                    onClick={() => handleMentionSelect(user)}
                  >
                    {user.email.slice(0, -8)}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end w-full mt-1">
            <button
              onClick={handleAddComment}
              disabled={!comment.trim()}
              className={`rounded-3xl px-3 py-1 text-xs font-bold tracking-tight leading-tight text-white ml-auto mr-4 ${
                comment.trim()
                  ? "bg-jiojbm50 hover:bg-jiojbm60"
                  : "bg-jiojbm60 opacity-40 cursor-not-allowed"
              }`}
              aria-label="Add Comment"
              type="button"
            >
              Add Comment
            </button>
          </div>
        </div>
        {/* Comments list — scrollable, newest first */}
        <div
          ref={commentsContainerRef}
          className="flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto thin-scrollbar mt-2 mr-2"
        >
          {sortedComments.map((c, index) => (
            <div key={index}>
              <div className="border p-2 mr-2 pl-3 border-gray60 rounded-lg">
                <div className="flex items-center">
                  <p className="font-semibold text-gray-600 mr-2">
                    {c.commentedBy}
                  </p>
                  <p className="text-xs pt-0.5 text-gray-500">
                    commented on{" "}
                    {c.createdAt &&
                      new Date(c.createdAt).toLocaleString()}
                  </p>
                </div>
                <div className="m-2">
                  {editingIndex === comments.length - 1 - index ? (
                    <textarea
                      id={`edit-input-${index}`}
                      className="w-full border p-2 rounded"
                      value={editedComment}
                      onChange={(e) => setEditedComment(e.target.value)}
                    />
                  ) : (
                    <p className="whitespace-pre-line break-words">
                      {highlightMentions(c.commentText)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
          <div ref={commentsEndRef} />
        </div>
      </div>
    </div>
  );
};
